import { Component } from "preact";
import queryString from "query-string";
import { connect } from "react-redux";
import BookingHeader from "../../components/BookingHeader";
import Content from "../../components/Content";
import Page from "../../components/Page";
import TwoCols from "../../components/TwoCols";
import YourOrder from "../../components/YourOrder";
import TrackingService from "../../services/tracking/TrackingService";
import { getEvent } from "../../state/actions/eventActions";
import { resetMerchOrder } from "../../state/actions/merchandiseActions";
import { clearOrder, requestOrder } from "../../state/actions/orderActions";
import { resetSeatingMapEvaluation } from "../../state/actions/seatingMapActions";
import {
  addPrice,
  addQuantity,
  checkAccessCode,
  loadTickets,
  selectDate,
  showCustomDonationField,
  updateDonationAmount
} from "../../state/actions/ticketsActions";
import { checkObjNotEmpty } from "../../utils/ObjectUtils";
import { pageChange } from "../../utils/BrowserUtils";
import AccessibilityComponent from "./components/AccessibilityComponent";
import DateSelectorComponent from "./components/DateSelectorComponent";
import OrderErrorMessage from "./components/OrderErrorMessage";
import SeatingMapModal from "./components/SeatingMapModal";
import TicketSelectionComponent from "./components/TicketSelectionComponent";
import EventDataChecks from "./components/EventDataChecks";
import { determineManualOrder } from "../../utils/ManualOrderUtils";
import { css } from "emotion";
import { max } from "../../utils/CssUtils";

const colSpacing = css({
  marginRight: 10,
  [max(700)]: {
    marginRight: 0
  }
});

class Tickets extends Component {
  constructor() {
    super();
    this.addTicketQuantity = this.addTicketQuantity.bind(this);
    this.addTicketPrice = this.addTicketPrice.bind(this);
    this.requestOrder = this.requestOrder.bind(this);
    this.state = {
      showModal: false
    };
  }

  componentDidMount() {
    const {
      event,
      order,
      getEvent,
      eventSlug,
      clearOrder,
      resetSeatingMapEvaluation,
      resetMerchOrder,
      selectDate
    } = this.props;
    resetMerchOrder();
    const parsed = queryString.parse(window.location.search);
    resetSeatingMapEvaluation();
    if (!event.data && !event.loading) {
      getEvent(eventSlug, null, false, null, false);
    } else if (event.data && event.data.slug !== eventSlug) {
      getEvent(eventSlug, null, false, null, false);
    }

    if (parsed.dateId) {
      let parsedDate;
      event.data.dates.forEach(date => {
        if (parsed.dateId === date._id) {
          parsedDate = date;
        }
      });
      selectDate(parsedDate._id, parsedDate.display, parsedDate.startDate);
    }
    if (order && order.data && order.data.status === "complete") {
      clearOrder();
    }
    pageChange("Select Tickets", event);
    TrackingService.ticketsViewed(event.data);
  }

  componentDidUpdate() {
    const { event, selectDate, ticketSelection, checkAccessCode } = this.props;
    const parsed = queryString.parse(window.location.search);
    const isManualOrder = determineManualOrder();
    const hasAppliedAccessCode =
      (ticketSelection.accessCode && parsed.accesscode) || !parsed.accesscode;
    if (
      event &&
      event.data &&
      event.data.validDates &&
      ticketSelection &&
      !ticketSelection.tickets &&
      hasAppliedAccessCode
    ) {
      if (isManualOrder) {
        selectDate(
          event.data.dates[0]._id,
          event.data.dates[0].display,
          event.data.dates[0].startDate
        );
      } else {
        if (event.data.validDates.length === 1) {
          selectDate(
            event.data.validDates[0]._id,
            event.data.validDates[0].display,
            event.data.validDates[0].startDate
          );
        }
      }
    }
    if (
      !ticketSelection.accessCode &&
      ticketSelection.accessCodeRequest &&
      !ticketSelection.accessCodeRequest.loading &&
      event &&
      event.data &&
      parsed.accesscode
    ) {
      checkAccessCode(event.data._id, parsed.accesscode, false);
    }
  }

  addTicketQuantity(ticketTypeId, quantity) {
    const { addQuantity } = this.props;
    addQuantity(ticketTypeId, quantity);
  }

  addTicketPrice(ticketTypeId, price) {
    const { addPrice } = this.props;
    addPrice(ticketTypeId, price);
  }

  closeModal = () => {
    this.setState({
      showModal: false
    });
  };

  openModal = () => {
    this.setState({
      showModal: true
    });
  };

  requestOrder() {
    const {
      eventSlug,
      event,
      order,
      ticketSelection,
      requestOrder
    } = this.props;
    const totalTickets = ticketSelection.tickets.reduce(
      (sum, { quantity }) => sum + quantity,
      0
    );
    if (
      !order.orderRequest.loading &&
      (totalTickets || ticketSelection.clientDonation)
    ) {
      const lastOrderId = order.data ? order.data._id : null;
      const parsed = queryString.parse(window.location.search);
      const discountCode = parsed.discountcode
        ? parsed.discountcode
        : order.discountCode;
      const manualOrderValues = {};
      if (window) {
        manualOrderValues.token = window.manualOrderToken;
        manualOrderValues.manualOrder = window.isManualOrder;
        manualOrderValues.manualOrderSettings = window.manualOrderSettings;
      }
      requestOrder(
        eventSlug,
        event.id,
        ticketSelection.selectedDateId,
        ticketSelection.requestedTickets,
        discountCode,
        ticketSelection.clientDonation,
        lastOrderId,
        false,
        manualOrderValues
      );
    }
  }

  render({
    event,
    order,
    ticketSelection,
    selectDate,
    updateDonationAmount,
    orderSummary,
    showCustomDonationField
  }) {
    const eventDataChecks = EventDataChecks(event);
    if (eventDataChecks) {
      return eventDataChecks;
    }
    const displayAccessibility =
      event.data && event.data.accessibility
        ? checkObjNotEmpty(event.data.accessibility)
        : false;
    const showSeatingMapOverview =
      event.data.seatingMaps &&
      event.data.seatingMaps.length > 0 &&
      event.data.seatingMaps.reduce((show, seatingMap) => {
        return show || seatingMap.public;
      }, false);
    return (
      <Page>
        <SeatingMapModal
          showSeatingMapOverview={showSeatingMapOverview}
          showModal={this.state.showModal}
          closeModal={this.closeModal}
        />
        <Content>
          <BookingHeader />
          <TwoCols>
            <div className={colSpacing}>
              <AccessibilityComponent
                displayAccessibility={displayAccessibility}
                event={event}
              />
              <OrderErrorMessage order={order} />
              <DateSelectorComponent
                datesUseCalendar={event.data.datesUseCalendar}
                event={event}
                ticketSelection={ticketSelection}
                selectDate={selectDate}
              />
              <TicketSelectionComponent
                event={event}
                ticketSelection={ticketSelection}
                addTicketPrice={this.addTicketPrice}
                addTicketQuantity={this.addTicketQuantity}
                requestOrder={this.requestOrder}
                orderSummary={orderSummary}
                order={order}
                updateDonationAmount={updateDonationAmount}
                showCustomDonationField={showCustomDonationField}
                openModal={this.openModal}
              />
            </div>
            <YourOrder allowDiscountsAdd={false} />
          </TwoCols>
        </Content>
      </Page>
    );
  }
}

export default connect(
  state => {
    return {
      event: state.event,
      ticketSelection: state.ticketSelection,
      order: state.order,
      orderSummary: state.orderSummary
    };
  },
  {
    getEvent,
    requestOrder,
    addQuantity,
    selectDate,
    loadTickets,
    clearOrder,
    updateDonationAmount,
    resetSeatingMapEvaluation,
    resetMerchOrder,
    showCustomDonationField,
    checkAccessCode,
    addPrice
  }
)(Tickets);



// WEBPACK FOOTER //
// ./routes/tickets/Tickets.js